import React from 'react';
import { Button } from './ui/button';
import { Calendar, Percent, Star, CheckCircle, ThumbsUp, Users } from 'lucide-react';

const HeroNew = ({ onOpenForm }) => {
  return (
    <section className="relative min-h-[100svh] flex flex-col overflow-hidden py-4 md:py-8">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1636873129833-f02760505743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxQb3J0byUyMFNlZ3Vyb3xlbnwwfHx8fDE3NzAzMzA4OTJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Porto Seguro Beach"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex flex-col h-full">
        
        {/* Top Section - Credibility Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
          <div className="flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 md:px-4 md:py-2 rounded-full shadow-lg">
            <Star className="w-3 h-3 md:w-5 md:h-5 fill-yellow-400 text-yellow-400" />
            <span className="font-bold text-gray-900 text-xs md:text-base">4.9</span>
            <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
          </div>
          <div className="flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 md:px-4 md:py-2 rounded-full shadow-lg">
            <ThumbsUp className="w-3 h-3 md:w-5 md:h-5 text-blue-600" />
            <span className="font-bold text-gray-900 text-xs md:text-base">98%</span>
          </div>
          <div className="flex items-center gap-1 bg-red-600 text-white px-2 py-1 md:px-4 md:py-2 rounded-full shadow-lg">
            <Users className="w-3 h-3 md:w-4 md:h-4" />
            <span className="text-xs md:text-sm font-bold">+28 mil atendidos</span>
          </div>
        </div>

        {/* Promo Call Text */}
        <div className="text-center mb-2 md:mb-4">
          <p className="text-sm md:text-xl text-white font-semibold">
            Preencha o formulário e tenha um{' '}
            <span className="text-yellow-400 font-bold animate-shimmer-text">
              desconto
            </span>{' '}
            Incrível!
          </p>
        </div>

        {/* Main Content - Centered */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="max-w-4xl mx-auto w-full">
            {/* Main Title */}
            <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight text-center">
              Viaje para <span className="text-red-500">Porto Seguro - BA</span>
            </h1>

            {/* Price Highlight */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border-2 md:border-4 border-red-500 shadow-2xl max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4">
                <div className="flex-1 text-center sm:text-left">
                  {/* Old Price - Crossed out */}
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                    <span className="text-gray-500 text-sm md:text-base">De</span>
                    <span className="text-gray-400 text-lg md:text-xl line-through">12x R$ 169,00</span>
                  </div>
                  
                  {/* New Price - Highlighted */}
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <span className="text-green-600 text-sm md:text-base font-semibold">Por</span>
                    <div className="flex items-baseline gap-1 md:gap-2">
                      <span className="text-4xl md:text-5xl font-bold text-red-600">12x</span>
                      <span className="text-3xl md:text-4xl font-bold text-gray-900">R$ 139,00</span>
                    </div>
                  </div>
                  
                  <p className="text-[10px] md:text-xs text-gray-600 mt-1">
                    OU <strong>R$ 1.390,00 À VISTA</strong> OU BOLETOS PARA QUITAR ANTES DA VIAGEM
                  </p>
                </div>
                
                {/* CTA Button with Wave Animation */}
                <div className="relative w-full sm:w-auto">
                  {/* Wave Animation Behind Button */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="absolute w-full h-full bg-green-400/30 rounded-full animate-ping-slow"></span>
                    <span className="absolute w-[90%] h-[90%] bg-green-400/20 rounded-full animate-ping-slower"></span>
                  </div>
                  
                  <Button
                    onClick={onOpenForm}
                    size="lg"
                    data-testid="hero-cta-button"
                    className="relative bg-green-600 hover:bg-green-700 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-bold rounded-full shadow-2xl w-full sm:w-auto z-10 animate-pulse-subtle"
                  >
                    <Percent className="mr-2 h-5 w-5" />
                    Garantir Meu Desconto
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Location */}
        <div className="mt-4 md:mt-8">
          <div className="bg-cyan-600/90 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4 max-w-xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-white" />
                <span className="text-white font-bold text-xs md:text-base">LOCAIS DE EMBARQUES:</span>
              </div>
              <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-center text-xs md:text-base">
                <span className="text-white font-semibold">BRASÍLIA - DF</span>
                <span className="text-white/60">/</span>
                <span className="text-white font-semibold">GOIÂNIA - GO</span>
                <span className="text-white/60">/</span>
                <span className="text-white font-semibold">ANÁPOLIS - GO</span>
                <span className="text-white/60 text-xs">E REGIÃO</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Element */}
      <div className="absolute bottom-0 left-0 right-0 h-8 md:h-16 bg-gradient-to-t from-white to-transparent z-10"></div>

      {/* Custom Animations CSS */}
      <style>{`
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.5; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        
        @keyframes ping-slower {
          0% { transform: scale(1); opacity: 0.4; }
          75%, 100% { transform: scale(1.8); opacity: 0; }
        }
        
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
        
        @keyframes shimmer-text {
          0%, 100% { opacity: 1; text-shadow: 0 0 10px rgba(250, 204, 21, 0.8); }
          50% { opacity: 0.8; text-shadow: 0 0 20px rgba(250, 204, 21, 1), 0 0 30px rgba(250, 204, 21, 0.5); }
        }
        
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        .animate-ping-slower {
          animation: ping-slower 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
          animation-delay: 0.5s;
        }
        
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        
        .animate-shimmer-text {
          animation: shimmer-text 1.5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default HeroNew;
